import { z } from "zod"
import { StoreCustomerJob } from "../customer/store-customer"
import { ServiceBikeType } from "../service/service-bike"
import { TireStorageListItem } from "../tire-storage/tire-storage"

/** One of a customer's bikes, with the jobs it has come in for. */
export interface StoreBikeListItem {
  id: string
  bikeBrandName: string | null
  model: string | null
  color: string | null
  type: ServiceBikeType
  customBikeTypeId: string | null
  customBikeTypeName: string | null
  isElectric: boolean
  numJobs: number
  latestHandInDate: Date | null
  /** Display numbers of the tire storages currently held for this bike. */
  storedTireDisplayNumbers: number[]
}

export interface StoreBikeDetail extends StoreBikeListItem {
  storeCustomerId: string
  customerName: string
  /** Sum of the order value of every job the bike has come in for. */
  totalSpent: number
  jobs: StoreCustomerJob[]
  tireStorages: TireStorageListItem[]
}

export const CreateStoreBikeSchema = z.object({
  type: z.nativeEnum(ServiceBikeType),
  /** Brand names are unique; the API looks the brand up. */
  bikeBrandName: z.string().nullable(),
  model: z
    .string()
    .trim()
    .nullable()
    .transform((value) => (value === "" ? null : value)),
  color: z.string().nullable(),
  isElectric: z.boolean(),
})
export type CreateStoreBike = z.infer<typeof CreateStoreBikeSchema>

export const MergeStoreBikesSchema = z.object({
  targetId: z.string().uuid(),
  sourceIds: z.array(z.string().uuid()).min(1),
  /** What the merged bike is, as settled by the mechanic. */
  result: CreateStoreBikeSchema.extend({
    customBikeTypeId: z.string().uuid().nullable(),
  }),
})
export type MergeStoreBikes = z.infer<typeof MergeStoreBikesSchema>

/**
 * What a set of bikes says about one field. An unknown value doesn't disagree
 * with anything, so only two different known values are a conflict.
 */
export const resolveMergedValue = <T>(
  values: (T | null)[],
): { value: T | null; isConflict: boolean } => {
  const known = [...new Set(values.filter((value) => value !== null))]
  return known.length > 1
    ? { value: null, isConflict: true }
    : { value: known[0] ?? null, isConflict: false }
}

/** "Crescent Nyx" — falls back to null when neither brand nor model is known. */
export const getStoreBikeName = (
  bike: Pick<StoreBikeListItem, "bikeBrandName" | "model">,
): string | null =>
  [bike.bikeBrandName, bike.model].filter((part) => part).join(" ") || null
